import { useEffect, useRef, useState } from 'react'

/**
 * Codul, in casute - una pentru fiecare cifra.
 *
 * -----------------------------------------------------------------------------
 * DE CE CASUTE SI NU UN CAMP
 * -----------------------------------------------------------------------------
 *
 * Intr-un camp singur nu se vede cate cifre ai tastat si cate mai ai, iar o
 * greseala la a patra se descopera abia dupa ce apesi. In casute se vede, si se
 * corecteaza acolo unde e greseala.
 *
 * Si nu se mai apasa nimic: cand se umple ultima, codul pleaca singur. E
 * ultimul lucru dintre om si ecranul lui de lucru, n-are de ce sa mai ceara o
 * apasare.
 *
 * -----------------------------------------------------------------------------
 * PATRU PURTARI CARE PAR MARUNTE SI NU SUNT
 * -----------------------------------------------------------------------------
 *
 *   1. UN SEIF DE PAROLE TOARNA TOT CODUL IN PRIMA CASUTA. iCloud Keychain si
 *      celelalte scriu toate cifrele deodata in campul cu `one-time-code`. Daca
 *      am lua doar primul caracter, codul completat automat n-ar merge
 *      niciodata - si omul n-ar sti de ce.
 *   2. LIPIREA se imparte la fel, din casuta in care s-a lipit incolo.
 *   3. BACKSPACE INTR-O CASUTA GOALA sterge inapoi. Altfel omul apasa si nu se
 *      intampla nimic, si trebuie sa nimereasca el casuta de dinainte.
 *   4. ATINSA, casuta se goleste. Cine se intoarce sa corecteze o cifra vrea sa
 *      o rescrie, nu sa o gaseasca acolo si sa o stearga intai.
 *
 * Toate patru sunt purtarea paginii vechi (`public/js/login_page.js`), pastrata
 * intocmai.
 */
export function CodeBoxes({
  length,
  disabled,
  onComplete,
}: {
  length: number
  disabled: boolean
  /** Chemat cand toate casutele sunt pline. */
  onComplete: (code: string) => void
}) {
  const [cells, setCells] = useState<string[]>(() => Array<string>(length).fill(''))
  const refs = useRef<Array<HTMLInputElement | null>>([])
  const wasDisabled = useRef(disabled)

  /* Dupa un cod gresit (verificarea s-a terminat fara sa plece de pe ecran),
     casutele se golesc si cursorul se intoarce la prima: altfel o singura
     cifra noua retrimitea codul gresit. */
  useEffect(() => {
    if (wasDisabled.current && !disabled) setCells(Array<string>(length).fill(''))
    wasDisabled.current = disabled
    if (!disabled) refs.current[0]?.focus()
  }, [disabled, length])

  function put(from: number, text: string): void {
    const digits = text.replace(/\D/g, '')
    if (digits === '') return

    const next = [...cells]
    let i = from
    for (const digit of digits) {
      if (i >= length) break
      next[i] = digit
      i += 1
    }
    setCells(next)

    /* Cursorul sta pe prima casuta inca goala, sau pe ultima scrisa. */
    refs.current[Math.min(i, length - 1)]?.focus()

    const code = next.join('')
    if (code.length === length && !next.includes('')) onComplete(code)
  }

  return (
    <div className="od-boxes" id="odBoxes">
      {cells.map((value, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el
          }}
          className="od-box"
          type="text"
          /* NU `maxLength={1}`: un seif de parole toarna tot codul aici, si cu
             o singura pozitie browserul l-ar tăia la prima cifra. Taierea o
             facem noi, in `put`, dupa ce am vazut tot. */
          inputMode="numeric"
          pattern="[0-9]*"
          autoCorrect="off"
          spellCheck={false}
          disabled={disabled}
          aria-disabled={disabled}
          aria-label={`Character ${i + 1} of ${length}`}
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          autoFocus={i === 0}
          value={value}
          onChange={(e) => {
            /* Cifra stearsa (Backspace / Delete intr-o casuta plina): casuta ramane goala. */
            if (e.target.value.replace(/\D/g, '') === '') {
              setCells((now) => now.map((c, j) => (j === i ? '' : c)))
              return
            }
            put(i, e.target.value)
          }}
          onFocus={(e) => {
            e.target.select()
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Backspace') return
            if (cells[i] !== '') return
            /* Goala: se sterge inapoi. */
            e.preventDefault()
            const next = [...cells]
            next[Math.max(0, i - 1)] = ''
            setCells(next)
            refs.current[Math.max(0, i - 1)]?.focus()
          }}
          onPaste={(e) => {
            e.preventDefault()
            put(i, e.clipboardData.getData('text'))
          }}
        />
      ))}
    </div>
  )
}
